import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core"
import { Message } from "@material-ui/icons"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import GetAppIcon from "@material-ui/icons/GetApp"
import {
  getBoardApplicantById,
  getStatusList,
  updateApplicantStatus,
} from "actions/Clerk/applicants"
import { getBoardApplicants } from "actions/Clerk/board"
import classnames from "clsx"
import ApplicantData from "components/Clerk/ApplicantData"
import AvatarComponent from "components/Common/Avatar"
import ButtonRounded from "components/Common/ButtonRounded"
import ChipSquare from "components/Common/ChipSquare"
import AppContext from "contexts/App"
import MessagesContext from "contexts/Messages"
import { isEmpty } from "lodash"
import PropTypes from "prop-types"
import { useContext } from "react"
import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"

import useStyles from "./styles"

const Step2 = ({
  step,
  currentApplication,
  openMutation,
  hide,
  setHide,
  closeFunction,
  setChangeStatus,
}) => {
  const classes = useStyles()

  const { openSnackBar, closeDialog, openDialog, authUser, isClerk } =
    useContext(AppContext)
  const { createThreadMutation, refetchThreads } = useContext(MessagesContext)

  const getApplicationByIdMutation = useMutation(getBoardApplicantById)
  const [loading, setLoading] = useState(false)
  const [moreData, setMoreData] = useState({})
  const [applicationData, setApplicationData] = useState({})
  const history = useHistory()
  const [selectedStatus, setSelectedStatus] = useState()
  const [anchorEl, setAnchorEl] = useState(null)
  const statusMutation = useMutation(updateApplicantStatus)

  const { refetch: refetchBoardApplicants } = useQuery(
    ["boardApplicants", currentApplication?.board_id],
    () => getBoardApplicants(currentApplication?.board_id)
  )

  useEffect(() => {
    if (step === 1) {
      setLoading(true)
      let data = {
        boardId: currentApplication?.board_id,
        applicationId: currentApplication?.application_id,
      }
      getApplicationByIdMutation
        .mutateAsync(data)
        .then((res) => {
          setSelectedStatus({
            label: res?.applicant?.status,
            id: res?.applicant?.status_id,
          })
          setApplicationData(res)
          //if the applicant is applied change to viewes when the clerk open this dialog
          if (res?.applicant?.status_id === 1) {
            let status = { status: "Viewed", id: 2 }
            handleCloseStatus(status)
          }
          setLoading(false)
        })
        .catch((err) => {
          Message({ message: "Error viewing application" })
          setLoading(false)
        })
    }
  }, [step])

  // open dialog with selections for info to be included in an applicant's application download
  const handleOpenDialog = () => {
    let data = {
      applicationId: currentApplication?.application_id,
      name: `${applicationData?.applicant?.first_name}_${applicationData?.applicant?.last_name}`.toLowerCase(),
    }
    openDialog("downloadApplicantProfile", data)
  }

  // used to download the resume or cover letter if a document was uploaded
  const downloadPdf = (id) => {
    openMutation
      ?.mutateAsync(id)
      .then((res) => {
        window.location.href = res?.url
      })
      .catch((err) => {
        openSnackBar({ message: "Error opening resume" })
      })
  }
  const goToBoard = () => {
    return new Promise((resolve, reject) => {
      history.push(`boards/${currentApplication?.board_id}`)
      resolve()
    })
  }

  const openStatus = (event) => {
    setAnchorEl(event.currentTarget)
  }

  //populate status dropdown
  const { data: statusOptions, isLoading: statusOptionsLoading } = useQuery(
    "status",
    getStatusList
  )

  const handleCloseStatus = (status) => {
    let data = {
      boardId: currentApplication?.board_id,
      applicationId: currentApplication?.application_id,
      status_id: status.id,
    }
    statusMutation
      .mutateAsync(data)
      .then((res) => {
        setChangeStatus(true)
        setAnchorEl(null)
        setSelectedStatus({ label: status.status, id: status.id })
        openSnackBar({ message: "Application status updated" })
        refetchBoardApplicants()
      })
      .catch((err) => {
        openSnackBar({ message: "Error updating application status" })
      })
  }
  const navigateToChat = async () => {
    // will ultimately handle going to chat with this citizen's id OR creaete new chat
    // for now basic routing to general messages
    try {
      const response = await createThreadMutation.mutateAsync({
        user_id: currentApplication.user_id,
        entity_id: authUser.entity_id,
        admin: isClerk,
      })
      await refetchThreads()

      history.push(`/messages/${response.thread_id}`)
    } catch (error) {
      throw new Error(error)
    }
    closeFunction()
  }

  return (
    <Box
      flexGrow={1}
      style={!isEmpty(applicationData) ? { alignSelf: "flex-start" } : null}
    >
      {isEmpty(applicationData) ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress color="primary" size={30} />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <AvatarComponent
                className={classes.avatar}
                user={applicationData?.applicant}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              flexGrow={1}
              marginLeft="10px"
            >
              <Typography className={classes.name} variant="h5">
                {applicationData?.applicant?.first_name +
                  " " +
                  applicationData?.applicant?.last_name}
              </Typography>

              <Typography>
                {applicationData?.applicant?.city}
                {", "}
                {applicationData?.applicant?.state}
                {", "}
                {applicationData?.applicant?.zip_code}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column">
              <ButtonRounded
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => navigateToChat()}
              >
                Start Chat
              </ButtonRounded>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <IconButton
              className={classes.downloadButton}
              onClick={handleOpenDialog}
            >
              <GetAppIcon className={classes.colorPink} />
            </IconButton>
            <ButtonRounded
              className={classnames(classes.root, {
                [classes.appliedChip]: selectedStatus?.id === 1,
                [classes.viewedChip]: selectedStatus?.id === 2,
                [classes.appointedChip]: selectedStatus?.id === 3,
                [classes.deferredChip]: selectedStatus?.id === 4,
                [classes.noLongerConsiderationChip]: selectedStatus?.id === 5,
                [classes.otherChip]: selectedStatus?.id > 5,
              })}
              onClick={openStatus}
              aria-controls={step ? "simple-menu" : undefined}
              aria-haspopup="true"
              disabled={selectedStatus?.id === 4}
              variant="contained"
            >
              {selectedStatus?.label} <ExpandMoreIcon />
            </ButtonRounded>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              // onClose={handleClose}
            >
              {statusOptions
                ?.filter((x) => x.id !== 1 && x.id !== 4 && x?.id !== 7)
                ?.map((s) => {
                  return (
                    <MenuItem key={s.id} onClick={() => handleCloseStatus(s)}>
                      {s.status}
                    </MenuItem>
                  )
                })}
            </Menu>
          </Box>
          {/* component reused in applicant dialog in boards*/}
          <ApplicantData
            applicantData={applicationData}
            setHide={setHide}
            setMoreData={setMoreData}
            downloadPdf={downloadPdf}
            moreData={moreData}
            openPdfMutation={openMutation}
            hide={hide}
            board={true}
          />
        </>
      )}
    </Box>
  )
}
Step2.propTypes = {
  step: PropTypes.number,
  currentApplication: PropTypes.object,
  openMutation: PropTypes.object,
  setHide: PropTypes.func,
  hide: PropTypes.bool,
  closeFunction: PropTypes.func,
  setChangeStatus: PropTypes.func,
}
export default Step2
